import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationIcon } from '@hero/web-icons';
import type { Id } from '@hero/schema';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../ui/Dialog/Dialog';
import { Button } from '../../../ui/Button/Button';
import { GoogleMapWithMarker } from '../GoogleMapWithMarker';
import type { MapLocationData } from '../types';

/**
 * A point the user picked, in the shape callers actually store — a
 * pin plus the human labels that go with it. Deliberately NOT
 * `MapLocationData`: consumers persist `lat`/`lng` and show a name,
 * they never want the map's internal address object.
 */
export interface ChosenLocation {
  lat: number;
  lng: number;
  /** Place name — the saved-place name, or the head of the address. */
  name?: string;
  fullAddress?: string;
  city?: string;
  /** The pin is an exact drop, not a city-level approximation. */
  isAccurate?: boolean;
  /** Set when the pick came from an existing saved place. */
  savedLocationId?: string;
  /** Google Maps share URL the pin was derived from, when there was one. */
  locationUrl?: string;
}

export interface ChooseLocationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Header, e.g. "Where is this place?". */
  title?: string;
  description?: string;
  /**
   * The pin to open on. When unset the map opens on the user's
   * current location and goes straight into pin-drop mode.
   */
  value?: ChosenLocation | null;
  /** The client the pin belongs to — scopes the autocomplete request. */
  clientId?: Id | string;
  /** Label for the primary action. Defaults to "Use this location". */
  confirmLabel?: string;
  onChoose: (location: ChosenLocation) => void;
}

/**
 * Choose a point on the map — search bar, "my current location", one
 * draggable pin.
 *
 * This is the lightweight *picker*: it hands a point back and
 * persists nothing. Reach for it whenever a flow needs coordinates
 * (agent confirmation cards, quick edits, anything that would
 * otherwise print `{"lat":24.15,"lng":47.32}` at a human). The
 * heavier {@link LocationPickerDrawer} / SavedLocation form remain
 * the right call when the goal is to *create a SavedLocation record*
 * with contacts, type, and client linking.
 *
 * Everything interactive comes from the shared map: the Google search
 * input and the current-location button live inside
 * `GoogleMapWithMarker`, so behaviour and RTL handling match the order
 * map and the saved-location form. Saved places are deliberately out
 * of the search here — this dialog picks a point, and offering an
 * existing place invites picking a *record* instead.
 *
 * Searching, clicking, and dragging all land straight in the draft.
 * The footer is the only commit: Save hands the point back, Cancel
 * restores the pin the dialog opened on.
 */
export const ChooseLocationModal = ({
  open,
  onOpenChange,
  title,
  description,
  value,
  clientId,
  confirmLabel,
  onChoose,
}: ChooseLocationModalProps) => {
  const { t } = useTranslation(['m-location', 'c-main']);
  const [draft, setDraft] = useState<ChosenLocation | null>(value ?? null);

  useEffect(() => {
    if (open) setDraft(value ?? null);
  }, [open, value]);

  const hasInitialPin = !!value && (value.lat !== 0 || value.lng !== 0);

  // Ref-stable: `locations` feeds the map's own state sync, and a new
  // array identity on every render re-enters that effect.
  const initialLocations = useMemo<MapLocationData[]>(
    () =>
      hasInitialPin && value
        ? [
            {
              cords: { lat: value.lat, lng: value.lng },
              fullAddress: value.fullAddress,
              city: value.city,
              isAccurateLocation: value.isAccurate ?? false,
              locationUrl: value.locationUrl,
            },
          ]
        : [],
    [hasInitialPin, value],
  );

  const mapConfig = useMemo(
    () => ({
      height: '100%',
      showUserCurrentLocation: true,
      disableFullscreen: true,
      markerIcon: 'normal_location_icon' as const,
      ...(hasInitialPin && value ? { center: { lat: value.lat, lng: value.lng } } : {}),
    }),
    [hasInitialPin, value],
  );

  const locationsConfig = useMemo(
    () => ({
      countLimit: 1,
      mode: 'add_new_location' as const,
      enableAccurateLocationIndicator: true,
      // No accuracy prompt: the user is looking at the pin they just
      // placed, and the footer's Save is the confirmation.
      enableAccurateLocationToggle: false,
      // Saving a place is the caller's job (and may need a client, a
      // type, contacts). The picker only picks.
      enableSavingPlaces: false,
      // The pin is draggable from the moment the dialog opens, and every
      // move lands in `draft` — the footer commits, nothing else.
      enterEditModeInitially: true,
      autoCommitEdits: true,
    }),
    [],
  );

  const clientConfig = useMemo(
    () => (clientId ? { thirdPartyCustomerId: clientId as Id } : undefined),
    [clientId],
  );

  const handleMapLocation = (location: MapLocationData) => {
    setDraft(toChosenLocation(location, draft?.name));
  };

  const summary =
    draft?.name?.trim() ||
    draft?.fullAddress?.trim() ||
    draft?.city?.trim() ||
    t('choose_location.no_pin_yet', { defaultValue: 'Drop a pin to continue' });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex h-[85vh] w-[95vw] max-w-5xl flex-col gap-0 overflow-hidden p-0'>
        <DialogHeader className='border-b px-5 py-3'>
          <DialogTitle>
            {title ?? t('choose_location.title', { defaultValue: 'Choose a location' })}
          </DialogTitle>
          <DialogDescription>
            {description ??
              t('choose_location.description', {
                defaultValue:
                  'Search for the place or drop the pin exactly where the truck should go.',
              })}
          </DialogDescription>
        </DialogHeader>

        <div className='relative min-h-0 flex-1'>
          <GoogleMapWithMarker
            searchMode='google-only'
            locations={initialLocations}
            mapConfig={mapConfig}
            locationsConfig={locationsConfig}
            clientConfig={clientConfig}
            onLocationChange={handleMapLocation}
            onLocationsReady={locations => {
              const last = locations[locations.length - 1];
              if (last) setDraft(toChosenLocation(last, draft?.name));
            }}
          />
        </div>

        <div className='flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3'>
          <span className='flex min-w-0 items-center gap-2 text-sm text-gray-700'>
            <LocationIcon className='size-4 shrink-0 text-primary' />
            <span className='truncate'>{summary}</span>
          </span>
          <span className='flex items-center gap-2'>
            <Button
              variant='ghost'
              onClick={() => {
                // Cancel means "forget everything I moved" — back to the
                // pin the dialog opened on.
                setDraft(value ?? null);
                onOpenChange(false);
              }}>
              {t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              disabled={!draft}
              onClick={() => {
                if (!draft) return;
                // Saving from the map *is* the exact-pin confirmation:
                // the user placed this pin and looked at it before
                // pressing Save.
                onChoose({ ...draft, isAccurate: true });
                onOpenChange(false);
              }}>
              {confirmLabel ??
                t('choose_location.use_location', { defaultValue: 'Use this location' })}
            </Button>
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Map shape → caller shape. The name falls back through the saved
 * place, then the head of the reverse-geocoded address, then the
 * city, so consumers always have something human to render — the
 * whole point of the picker is that nobody has to read coordinates.
 */
const toChosenLocation = (
  location: MapLocationData,
  previousName?: string,
): ChosenLocation => {
  const savedName = location.savedLocation?.name?.trim();
  const addressHead = location.fullAddress?.split(',')[0]?.trim();
  return {
    lat: location.cords.lat,
    lng: location.cords.lng,
    name: savedName || addressHead || location.city?.trim() || previousName,
    fullAddress: location.fullAddress,
    city: location.city,
    isAccurate: location.isAccurateLocation ?? false,
    savedLocationId: location.savedLocation?._id
      ? String(location.savedLocation._id)
      : undefined,
    locationUrl: location.locationUrl,
  };
};
